import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { 
  PhoneIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  CalendarIcon,
  MapPinIcon,
  UserGroupIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://sayhi-voice-engine-production.up.railway.app'

export default function MyBusiness() {
  const { userBusinesses, isSuperAdmin, getUserBusinessPhones } = useAuth()
  const [stats, setStats] = useState({
    totalCalls: 0,
    todayCalls: 0,
    emergencyCalls: 0,
    completedCalls: 0,
    avgResponseTime: 0,
    popularServices: []
  })
  const [recentCalls, setRecentCalls] = useState([])
  const [businessInfo, setBusinessInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [timeframe, setTimeframe] = useState('today')
  const [selectedCall, setSelectedCall] = useState(null)
  const [showCallModal, setShowCallModal] = useState(false)

  // Get the first business for this user
  const myBusiness = userBusinesses?.[0]?.business_phone

  useEffect(() => {
    if (myBusiness) {
      loadBusinessData()
    } else if (!isSuperAdmin()) {
      setLoading(false)
    }
  }, [myBusiness, timeframe])

  const getAuthToken = () => {
    return localStorage.getItem('supabase_token')
  }

  const loadBusinessData = async () => {
    setLoading(true)
    setError(null)
    try {
      const authToken = getAuthToken()
      
      if (!authToken) {
        throw new Error('No authentication token available')
      }

      console.log('🌐 Frontend: Loading business data for:', myBusiness)
      console.log('🌐 Frontend: Using backend URL:', BACKEND_URL)

      // Load business info and stats from backend with CORRECT endpoints
      const params = new URLSearchParams({ timeframe })
      
      const [businessResponse, statsResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dashboard/business-settings/${myBusiness}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        }),
        // Fix: Use correct endpoint name
        fetch(`${BACKEND_URL}/api/dashboard/business-stats/${myBusiness}?${params}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          }
        })
      ])

      console.log('📡 Frontend: Business response status:', businessResponse.status)
      console.log('📡 Frontend: Stats response status:', statsResponse.status)

      if (!businessResponse.ok) {
        const errorText = await businessResponse.text()
        console.error('❌ Frontend: Business API error:', errorText)
        throw new Error(`Business API error: ${businessResponse.status} - ${errorText}`)
      }

      if (!statsResponse.ok) {
        const errorText = await statsResponse.text()
        console.error('❌ Frontend: Stats API error:', errorText)
        throw new Error(`Stats API error: ${statsResponse.status} - ${errorText}`)
      }

      const businessData = await businessResponse.json()
      const statsData = await statsResponse.json()

      console.log('📊 Frontend: Business data:', businessData)
      console.log('📊 Frontend: Stats data:', statsData)

      if (!businessData.success) {
        throw new Error(businessData.error || 'Failed to load business info')
      }

      if (!statsData.success) {
        throw new Error(statsData.error || 'Failed to load business stats')
      }

      console.log('✅ Frontend: Business data loaded successfully')
      setBusinessInfo(businessData.business)
      setStats(statsData.stats)
      setRecentCalls(statsData.recentCalls || [])

    } catch (error) {
      console.error('❌ Frontend: Error loading business data:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Unknown Number'
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1)
      return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`
    }
    return phone
  }

  const formatCallTime = (dateString) => {
    if (!dateString) return 'Unknown time'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    
    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      return `${diffMinutes}m ago`
    } else if (diffHours < 24) {
      return `${diffHours}h ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const getCallDuration = (call) => {
    const seconds = call.call_duration_seconds || call.duration_seconds || 0
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const openCallDetails = (call) => {
    setSelectedCall(call)
    setShowCallModal(true)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100'
      case 'in_progress': return 'text-blue-600 bg-blue-100'
      case 'failed': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getPriorityColor = (call) => {
    if (call.emergency_escalation) return 'bg-red-100 text-red-800 border-red-200'
    if (call.call_type === 'escalation') return 'bg-orange-100 text-orange-800 border-orange-200'
    return 'bg-green-100 text-green-800 border-green-200'
  }

  const getPriorityLabel = (call) => {
    if (call.emergency_escalation) return 'Urgent'
    if (call.call_type === 'escalation') return 'Escalated'
    return 'Normal'
  }

  // Check if calendar is connected based on nylas_grant_id
  const isCalendarConnected = businessInfo?.nylas_grant_id ? true : false

  // Redirect super admins to main dashboard
  if (isSuperAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Admin Access</h2>
          <p className="text-gray-600 mb-4">You have access to all businesses through the main dashboard.</p>
          <a href="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Main Dashboard →</a>
        </div>
      </div>
    )
  }

  // No business access
  if (!myBusiness) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Business Access</h2>
          <p className="text-gray-600">You don't have access to any businesses. Contact your administrator.</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Business Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadBusinessData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b">
        <div className="px-6 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <span className="text-white text-lg font-bold">
                  {businessInfo?.business_name?.charAt(0) || 'B'}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {businessInfo?.business_name || 'My Business'}
                </h1>
                <p className="text-gray-600 flex items-center space-x-2">
                  <span>{businessInfo?.business_type}</span>
                  <span>•</span>
                  <PhoneIcon className="h-4 w-4" />
                  <span>{formatPhoneNumber(businessInfo?.phone_number)}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
              <button
                onClick={loadBusinessData}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Business Status */}
          <div className={`mt-4 p-4 rounded-lg border ${
            businessInfo?.active 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  businessInfo?.active ? 'bg-green-400' : 'bg-red-400'
                }`}></div>
                <span className={`font-medium ${
                  businessInfo?.active ? 'text-green-800' : 'text-red-800'
                }`}>
                  Your AI Assistant is {businessInfo?.active ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
              {businessInfo?.active && (
                <div className="flex items-center space-x-4 text-sm text-green-700">
                  <span>✅ Answering calls</span>
                  <span>•</span>
                  <span>🚨 Emergency service: {businessInfo?.emergency_service ? 'Available' : 'Not available'}</span>
                  <span>•</span>
                  <span>📅 Calendar: {isCalendarConnected ? 'Connected' : 'Not connected'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="p-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Total Calls</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalCalls}</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <PhoneIcon className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Today</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.todayCalls}</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Urgent</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.emergencyCalls}</p>
                </div>
                <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Completed</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.completedCalls}</p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Avg Response</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.avgResponseTime}s</p>
                </div>
                <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <ClockIcon className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Recent Calls */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Recent Customer Calls</h3>
              </div>
              <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {recentCalls.length === 0 ? (
                  <div className="p-8 text-center">
                    <PhoneIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500">No recent calls</p>
                  </div>
                ) : (
                  recentCalls.map((call) => (
                    <div 
                      key={call.call_id} 
                      className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => openCallDetails(call)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <PhoneIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="font-medium text-gray-900">
                                {formatPhoneNumber(call.customer_phone)}
                              </span>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(call)}`}>
                                {getPriorityLabel(call)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {call.conversation_summary ? 
                                call.conversation_summary.substring(0, 80) + (call.conversation_summary.length > 80 ? '...' : '') : 
                                'Tap to see call details'
                              }
                            </p>
                            {call.services_mentioned && call.services_mentioned.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {call.services_mentioned.slice(0, 2).map((service, i) => (
                                  <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {service}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">{formatCallTime(call.call_start_time)}</p>
                          <p className="text-xs text-gray-400">{getCallDuration(call)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Business Info & Popular Services */}
            <div className="space-y-6">
              {/* Business Info */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Business Information</h3>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center space-x-3">
                    <UserGroupIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Owner</p>
                      <p className="text-sm text-gray-600">{businessInfo?.owner_name || 'Not specified'}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Contact</p>
                      <p className="text-sm text-gray-600">{formatPhoneNumber(businessInfo?.owner_phone)}</p>
                    </div>
                  </div>

                  {businessInfo?.address && (
                    <div className="flex items-center space-x-3">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Address</p>
                        <p className="text-sm text-gray-600">{businessInfo.address}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <ClockIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Hours</p>
                      <p className="text-sm text-gray-600">{businessInfo?.hours || 'Not specified'}</p>
                    </div>
                  </div>

                  {businessInfo?.service_area && (
                    <div className="flex items-center space-x-3">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Service Area</p>
                        <p className="text-sm text-gray-600">{businessInfo.service_area}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Popular Services */}
              {stats.popularServices && stats.popularServices.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Popular Services</h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      {stats.popularServices.map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">{item.service}</span>
                          <span className="text-sm text-gray-500">{item.count} requests</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Calendar Integration Status */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Calendar Integration</h3>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <CalendarIcon className="h-8 w-8 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {isCalendarConnected ? 'Connected' : 'Not Connected'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {isCalendarConnected 
                            ? `${businessInfo?.calendar_provider || 'Calendar'} integration active`
                            : 'Connect your calendar to enable automatic booking'
                          }
                        </p>
                        {isCalendarConnected && businessInfo?.calendar_email && (
                          <p className="text-xs text-gray-400 mt-1">
                            {businessInfo.calendar_email}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className={`h-3 w-3 rounded-full ${
                      isCalendarConnected ? 'bg-green-400' : 'bg-gray-400'
                    }`}></div>
                  </div>
                  {isCalendarConnected && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg">
                      <div className="text-sm text-green-700">
                        <p>✅ Automatic appointment booking enabled</p>
                        <p>✅ Real-time availability checking</p>
                        <p>✅ No double bookings</p>
                      </div>
                    </div>
                  )}
                  {!isCalendarConnected && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Connect your calendar in Settings to enable automatic appointment booking.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call Details Modal */}
      {showCallModal && selectedCall && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Call Details</h3>
              <button
                onClick={() => setShowCallModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Call Information */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Call Information</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Customer:</span>
                      <span className="font-medium">{formatPhoneNumber(selectedCall.customer_phone)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium">{getPriorityLabel(selectedCall)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">{getCallDuration(selectedCall)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">When:</span>
                      <span className="font-medium">
                        {selectedCall.call_start_time ? new Date(selectedCall.call_start_time).toLocaleString() : 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedCall.services_mentioned && selectedCall.services_mentioned.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Services Discussed</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCall.services_mentioned.map((service, i) => (
                        <span key={i} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Conversation Summary */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
                    Call Summary
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    {selectedCall.conversation_summary || 'No summary available for this call.'}
                  </p>
                </div>

                {selectedCall.customer_sentiment && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Customer Satisfaction</h4>
                    <div className="flex items-center space-x-2">
                      <div className={`h-3 w-3 rounded-full ${
                        selectedCall.customer_sentiment === 'positive' ? 'bg-green-400' :
                        selectedCall.customer_sentiment === 'negative' ? 'bg-red-400' : 'bg-yellow-400'
                      }`}></div>
                      <span className="capitalize font-medium">
                        {selectedCall.customer_sentiment || 'Neutral'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Full Transcript */}
            {selectedCall.transcript && (
              <div className="mt-6 bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2" />
                  Full Conversation
                </h4>
                <div className="max-h-60 overflow-y-auto bg-white rounded border p-3">
                  <div className="text-sm text-gray-700 space-y-2">
                    {selectedCall.transcript.split('\n').map((line, i) => (
                      <div key={i} className="leading-relaxed">
                        {line.includes('Agent:') ? (
                          <div className="text-blue-800 font-medium">{line}</div>
                        ) : line.includes('Customer:') ? (
                          <div className="text-green-800 font-medium">{line}</div>
                        ) : (
                          <div>{line}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCallModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}